/**
 * @fileoverview Input/output schemas for the chapter-planner-composite-v1 composite.
 *
 * The composite orchestrates chapter-planner-v2 (skeleton) → key-events-expander-v2
 * (beat-sheet expansion from seed) and returns a single ChapterIdea with the
 * expanded key events already applied.
 */

import { z } from 'zod';
import { CompositeBaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';
import { ChapterIdeaSchema } from '@/domains/chapter-workspace/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = z.object({
  ...CompositeBaseFlowInputSchema.shape,
  // Shared context (used by both steps)
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.userMessage.schema.shape,
  // Planner-step context
  ...AGENT_SCHEMA_OBJECTS.loreContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.seriesContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterMetadata.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.actualLastThreeChaptersContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.charactersCurrent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.worldElements.schema.shape,
  // Expander-step chapter-boundary context
  ...AGENT_SCHEMA_OBJECTS.previousChapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.nextChapterContext.schema.shape,
  isReplan: z.boolean().optional().describe(
    'When true, replans an existing chapter instead of planning a new next chapter.'
  ),
  chapterNumber: z.number().describe(
    'The chapter number the expander should treat as the active chapter. For a new chapter this is the next number after the last existing chapter; for a replan it is the existing chapter number.'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  suggestion: ChapterIdeaSchema.describe(
    'The planned chapter with key events expanded into a full beat sheet.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
